import { Asset } from '@/api/generatedApi';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CROSS_TENANT_TEXT_SEARCH_QUERY_KEY } from '../crossTenant/textsearch/query';
import { CROSS_TENANT_VISUAL_SEARCH_QUERY_KEY } from '../crossTenant/visualSearch/query';
import { CROSS_TENANT_ASSETS_GEOMETRIC_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/assetsGeometricSearch/query';
import { CROSS_TENANT_ASSETS_PART_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/assetsPartSearch/query';
import { CROSS_TENANT_ASSETS_VISUAL_SEARCH_QUERY_KEY } from '@/hooks/api/crossTenant/assetsVisualSearch/query';
import { GET_ASSET_QUERY_KEY } from './useGetAsset';
import { LIST_ASSETS_BY_STATE_QUERY_KEY } from './useFetchAssetsByState';
import { ASSET_STATE_COUNTS_QUERY_KEY } from './useGetAssetStateCounts';
import { GET_FOLDER_HIERARCHY_QUERY_KEY } from '../folders/useFolderHierarchy';

export const useDeleteAsset = ({
  asset,
  onSuccess,
  onError,
}: {
  asset?: Asset;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const { mutate, isPending, error } = useMutation({
    mutationKey: ['deleteAsset', asset?.id],
    mutationFn: () => api.assets.deleteAsset(tenantId, asset?.id ?? ''),
    onSuccess: () => {
      onSuccess?.();
      queryClient.resetQueries({
        queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [ASSET_STATE_COUNTS_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [CROSS_TENANT_TEXT_SEARCH_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [CROSS_TENANT_ASSETS_PART_SEARCH_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [CROSS_TENANT_ASSETS_GEOMETRIC_SEARCH_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [CROSS_TENANT_VISUAL_SEARCH_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [CROSS_TENANT_ASSETS_VISUAL_SEARCH_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({ queryKey: [GET_ASSET_QUERY_KEY, { assetId: asset?.id ?? '' }] });
      queryClient.resetQueries({ queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY], exact: false });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error.response?.data.message ?? 'Failed to delete asset');
    },
  });

  return {
    mutate,
    isPending,
    error,
  };
};
